import { useState, useEffect, useRef, type KeyboardEvent } from 'react'
import { MapPin, Plane, Train, Mountain, X, Loader2, Check } from 'lucide-react'
import {
  fetchPlaceSuggestions,
  getPlaceLocationDetails,
  type PlaceSuggestion,
} from '../../../../services/googleMapsPlaces'
import type { LocationGeoData } from '../../../../utils/salzburgLocation'

export interface DashboardLocationInputProps {
  label: string
  required?: boolean
  value: string
  geoValue?: LocationGeoData | null
  placeholder?: string
  isRestrictedToSalzburg?: boolean
  onSelect: (location: LocationGeoData) => void
  onClear?: () => void
  onChangeText?: (text: string) => void
  helperText?: string
}

export const DashboardLocationInput = ({
  label,
  required = false,
  value,
  geoValue,
  placeholder = 'Search address, hotel or airport...',
  isRestrictedToSalzburg = false,
  onSelect,
  onClear,
  onChangeText,
  helperText,
}: DashboardLocationInputProps) => {
  const [query, setQuery] = useState(value || '')
  const [isOpen, setIsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    setQuery(value || '')
  }, [value])

  // Debounced place suggestions fetching
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    const trimmed = query.trim()
    if (!isOpen || trimmed.length < 2) {
      setSuggestions([])
      setIsLoading(false)
      setSelectedIndex(-1)
      return
    }

    setIsLoading(true)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const results = await fetchPlaceSuggestions(trimmed, {
          restrictToSalzburg: Boolean(isRestrictedToSalzburg),
        })
        setSuggestions(results)
      } catch (err) {
        console.error('Failed to fetch suggestions:', err)
        setSuggestions([])
      } finally {
        setIsLoading(false)
      }
    }, 250)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [query, isOpen, isRestrictedToSalzburg])

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSuggestions([])
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = async (suggestion: PlaceSuggestion) => {
    setIsLoading(true)
    try {
      const full = await getPlaceLocationDetails(suggestion)
      onSelect(full)
      setQuery(full.title)
    } catch {
      const fallback: LocationGeoData = {
        title: suggestion.title,
        address: suggestion.address,
        isSalzburg: suggestion.isSalzburg,
      }
      onSelect(fallback)
      setQuery(suggestion.title)
    } finally {
      setIsLoading(false)
      setIsOpen(false)
      setSuggestions([])
    }
  }

  const handleClear = () => {
    setQuery('')
    setSuggestions([])
    setIsOpen(false)
    if (onClear) {
      onClear()
    } else if (onChangeText) {
      onChangeText('')
    }
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'ArrowDown') {
        setIsOpen(true)
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        handleSelect(suggestions[selectedIndex])
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const renderIcon = (suggestion: PlaceSuggestion) => {
    if (suggestion.iconType === 'airport') {
      return <Plane className="size-4 text-[#3B5998] shrink-0" />
    }
    if (suggestion.iconType === 'train') {
      return <Train className="size-4 text-[#FF6A1A] shrink-0" />
    }
    if (suggestion.iconType === 'mountain') {
      return <Mountain className="size-4 text-[#10B981] shrink-0" />
    }
    return <MapPin className="size-4 text-[#9E9E9E] shrink-0" />
  }

  return (
    <div className="flex flex-col gap-1.5 relative" ref={containerRef}>
      <div className="flex items-center justify-between">
        <label className="font-host text-[13px] font-medium text-[#757575]">
          {label} {required && <span className="text-[#FF6A1A]">*</span>}
        </label>
        {isRestrictedToSalzburg && (
          <span className="font-host text-[11px] font-medium text-[#FF6A1A] bg-[#FFF2EB] px-2 py-0.5 rounded-full">
            Salzburg Region only
          </span>
        )}
      </div>

      <div className="relative">
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9E9E9E]">
          <MapPin size={16} />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={placeholder}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
            if (onChangeText) {
              onChangeText(e.target.value)
            }
          }}
          onKeyDown={handleKeyDown}
          className={`w-full h-[48px] rounded-[8px] border bg-white pl-10 pr-10 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none transition-colors ${
            isOpen ? 'border-[#FF6A1A] ring-1 ring-[#FF6A1A]/20' : 'border-[#E0E0E0] hover:border-[#BDBDBD]'
          }`}
        />

        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          {isLoading && <Loader2 size={16} className="animate-spin text-[#9E9E9E]" />}
          {query && !isLoading && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-[#9E9E9E] hover:text-[#1A1A1A] transition-colors rounded-full"
              title="Clear location"
            >
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      {helperText && !isOpen && (
        <span className="font-host text-[11px] text-[#9E9E9E]">{helperText}</span>
      )}

      {/* Selected Location info pill */}
      {geoValue && geoValue.address && !isOpen && (
        <div className="flex items-center gap-1.5 text-[11px] font-host text-[#616161] bg-[#F7F7F7] px-2.5 py-1 rounded-[6px] border border-[#EEEEEE] truncate">
          <Check size={12} className="text-[#10B981] shrink-0" />
          <span className="truncate">{geoValue.address}</span>
          {geoValue.isSalzburg && (
            <span className="shrink-0 bg-[#E8F5E9] text-[#2E7D32] px-1.5 py-0.2 rounded text-[10px] font-semibold">
              Salzburg
            </span>
          )}
        </div>
      )}

      {/* Suggestions Dropdown */}
      {isOpen && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-50 rounded-[12px] border border-[#E0E0E0] bg-white shadow-[0_12px_32px_rgba(0,0,0,0.14)] overflow-hidden">
          {isRestrictedToSalzburg && (
            <div className="px-3.5 py-2 bg-[#FFF8F4] border-b border-[#FFE8DB] flex items-center justify-between text-[11px] font-host text-[#D9530F]">
              <span>Mutual rule: At least one location must be in Salzburg</span>
            </div>
          )}

          <div className="max-h-[260px] overflow-y-auto divide-y divide-[#F0F0F0]">
            {isLoading && suggestions.length === 0 && (
              <div className="flex items-center justify-center gap-2 py-6 text-[13px] font-host text-[#757575]">
                <Loader2 size={16} className="animate-spin text-[#FF6A1A]" />
                <span>Searching locations...</span>
              </div>
            )}

            {!isLoading && suggestions.length === 0 && query.trim().length >= 2 && (
              <div className="py-6 px-4 text-center">
                <p className="font-host text-[13px] text-[#757575]">No matching locations found.</p>
                <p className="font-host text-[11px] text-[#9E9E9E] mt-0.5">
                  Try typing city, hotel, address or airport code
                </p>
              </div>
            )}

            {suggestions.map((suggestion, idx) => {
              const isHighlighted = idx === selectedIndex
              return (
                <button
                  key={suggestion.placeId || `${suggestion.title}-${idx}`}
                  type="button"
                  onClick={() => handleSelect(suggestion)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`w-full text-left px-3.5 py-2.5 flex items-start gap-2.5 transition-colors ${
                    isHighlighted ? 'bg-[#F5F8FF]' : 'hover:bg-[#F9F9F9]'
                  }`}
                >
                  <div className="mt-0.5">{renderIcon(suggestion)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-host text-[13px] font-bold text-[#1A1A1A] truncate">
                        {suggestion.title}
                      </span>
                      {suggestion.isSalzburg && (
                        <span className="shrink-0 bg-[#E8F5E9] text-[#2E7D32] px-1.5 py-0.2 rounded text-[10px] font-semibold">
                          Salzburg
                        </span>
                      )}
                    </div>
                    {suggestion.address && (
                      <p className="font-host text-[12px] text-[#757575] truncate mt-0.5">
                        {suggestion.address}
                      </p>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
